import React, { useEffect, useMemo, useState } from "react";

type CourseCategory =
  | "All"
  | "School Foundation"
  | "JEE"
  | "NEET"
  | "Full-Stack"
  | "Algo Trading"
  | "Test Series"
  | "Mentorship";

type Course = {
  title: string;
  seoTitle: string;
  category: Exclude<CourseCategory, "All">;
  desc: string;
  duration: string;
  level: string;
  price: string;
  tag?: string;
  features: string[];
  audience: string;
  keywords: string;
  slug: string;
};

const categories: CourseCategory[] = [
  "All",
  "School Foundation",
  "JEE",
  "NEET",
  "Full-Stack",
  "Algo Trading",
  "Test Series",
  "Mentorship",
];

const categorySeoText: Record<CourseCategory, string> = {
  All: "All online courses for school students, JEE Main aspirants, NEET UG aspirants, coding learners, test series users and mentorship seekers.",
  "School Foundation":
    "School foundation courses for Class 7, Class 8, Class 9 and Class 10 students with Maths, Science, Reasoning, CBSE, NCERT and Olympiad preparation.",
  JEE: "JEE Main and JEE Advanced preparation courses for Physics, Chemistry, Mathematics, IIT entrance exam practice, mock tests and rank improvement.",
  NEET: "NEET UG preparation courses for Physics, Chemistry, Biology, NCERT revision, medical entrance exam practice and NEET mock tests.",
  "Full-Stack":
    "Full-stack web development courses covering React, TypeScript, Java Spring Boot, Node.js, REST APIs, MySQL, deployment and real projects.",
  "Algo Trading":
    "Algo trading course for beginners covering trading automation, strategy building, risk management, dashboards and market logic.",
  "Test Series":
    "Online test series for JEE, NEET, board exams, chapter-wise tests, full-length mock tests, score analysis and rank reports.",
  Mentorship:
    "Personal mentorship programs for students with study roadmap, weekly review, academic guidance, doubt support and progress tracking.",
};

const courses: Course[] = [
  {
    title: "Class 7-8 Foundation Batch",
    seoTitle: "Class 7 and Class 8 Online Foundation Course for Maths and Science",
    category: "School Foundation",
    desc: "Build a strong academic base in Maths, Science, Reasoning, English and school-level concepts with live classes, notes and regular practice.",
    duration: "6 Months",
    level: "Beginner",
    price: "₹2,999",
    tag: "Foundation",
    features: [
      "Live interactive classes",
      "Chapter-wise study notes",
      "Weekly practice tests",
      "Doubt support",
    ],
    audience:
      "Best for Class 7 and Class 8 students who want strong school exam preparation, NCERT concept clarity and early competitive foundation.",
    keywords:
      "Class 7 online classes, Class 8 online tuition, Class 7 Maths course, Class 8 Science course, school foundation course, NCERT foundation, online tuition for middle school, Maths Science Reasoning course",
    slug: "class-7-8-foundation-batch",
  },
  {
    title: "Class 9-10 Foundation + Boards",
    seoTitle: "Class 9 and Class 10 Foundation Course with CBSE Board Preparation",
    category: "School Foundation",
    desc: "Complete NCERT preparation for Class 9 and Class 10 with board exam practice, Science, Maths, assignments and early competitive foundation.",
    duration: "9 Months",
    level: "School Level",
    price: "₹4,499",
    tag: "Popular",
    features: [
      "Complete NCERT coverage",
      "CBSE board test practice",
      "Regular assignments",
      "Mentor guidance",
    ],
    audience:
      "Best for Class 9 and Class 10 students preparing for CBSE board exams, school tests, Olympiad basics and future JEE or NEET foundation.",
    keywords:
      "Class 9 online classes, Class 10 online coaching, CBSE Class 10 board preparation, NCERT Maths Science course, Class 10 Maths tuition, Class 10 Science coaching, board exam online course, school exam preparation",
    slug: "class-9-10-foundation-boards",
  },
  {
    title: "JEE Main Foundation Batch",
    seoTitle: "JEE Main Online Coaching for Physics Chemistry and Mathematics",
    category: "JEE",
    desc: "Structured JEE Main preparation with Physics, Chemistry, Mathematics, DPPs, live classes, mock tests and performance reports.",
    duration: "12 Months",
    level: "Intermediate",
    price: "₹7,999",
    tag: "JEE",
    features: [
      "PCM live classes",
      "Daily practice problems",
      "JEE mock tests",
      "Performance reports",
    ],
    audience:
      "Best for students who want JEE Main preparation, IIT entrance exam foundation, PCM concept clarity and regular exam pattern practice.",
    keywords:
      "JEE Main online coaching, IIT JEE preparation, Physics Chemistry Maths course, JEE PCM classes, JEE Main mock test, JEE foundation batch, engineering entrance coaching, online JEE course",
    slug: "jee-main-foundation-batch",
  },
  {
    title: "JEE Advanced Problem Solving",
    seoTitle: "JEE Advanced Problem Solving Course for IIT Entrance Preparation",
    category: "JEE",
    desc: "Advanced-level problem solving for IIT-JEE aspirants with topic-wise practice, difficult questions, expert sessions and rank improvement strategy.",
    duration: "6 Months",
    level: "Advanced",
    price: "₹6,999",
    tag: "Advanced",
    features: [
      "Advanced-level questions",
      "Topic-wise tests",
      "Rank improvement strategy",
      "Expert problem-solving sessions",
    ],
    audience:
      "Best for serious JEE aspirants who already know basics and want to improve speed, accuracy and advanced problem-solving ability.",
    keywords:
      "JEE Advanced course, IIT JEE Advanced preparation, advanced Physics questions, advanced Maths problems, JEE rank improvement, IIT entrance problem solving, JEE Advanced mock practice",
    slug: "jee-advanced-problem-solving",
  },
  {
    title: "NEET UG Target Batch",
    seoTitle: "NEET UG Online Coaching for Physics Chemistry and Biology",
    category: "NEET",
    desc: "Complete NEET UG preparation with Physics, Chemistry, Biology, NCERT-focused learning, full mock tests, revision and doubt solving.",
    duration: "12 Months",
    level: "Intermediate",
    price: "₹7,999",
    tag: "NEET",
    features: [
      "PCB live classes",
      "NCERT-focused revision",
      "Full-length mock tests",
      "Doubt solving support",
    ],
    audience:
      "Best for medical entrance aspirants preparing for NEET UG with NCERT Biology, Physics numericals, Chemistry concepts and test practice.",
    keywords:
      "NEET UG online coaching, NEET preparation course, NEET Biology classes, Physics Chemistry Biology NEET, medical entrance coaching, NEET mock test series, NCERT NEET revision, NEET target batch",
    slug: "neet-ug-target-batch",
  },
  {
    title: "NEET Biology Mastery",
    seoTitle: "NEET Biology NCERT Mastery Course for Medical Entrance Exam",
    category: "NEET",
    desc: "A focused Biology course for NEET aspirants covering NCERT line-by-line study, diagrams, revision, quizzes and repeated practice.",
    duration: "4 Months",
    level: "Focused",
    price: "₹3,499",
    tag: "Biology",
    features: [
      "NCERT line-by-line study",
      "Diagram practice",
      "Biology quiz sessions",
      "Revision notes",
    ],
    audience:
      "Best for NEET aspirants who want to strengthen Biology, NCERT reading, Human Physiology, Genetics, Ecology and diagram-based questions.",
    keywords:
      "NEET Biology course, NCERT Biology for NEET, Biology revision notes, NEET Biology quiz, Human Physiology NEET, Genetics NEET, Ecology NEET, Biology diagram practice",
    slug: "neet-biology-mastery",
  },
  {
    title: "Full-Stack Web Development",
    seoTitle: "Full-Stack Web Development Course with React TypeScript Spring Boot and Node.js",
    category: "Full-Stack",
    desc: "Learn frontend, backend, APIs, database, authentication, deployment and real project development with modern full-stack technologies.",
    duration: "6 Months",
    level: "Beginner to Advanced",
    price: "₹4,900",
    tag: "Best Seller",
    features: [
      "React and TypeScript",
      "Java Spring Boot",
      "Node.js APIs",
      "Live projects",
    ],
    audience:
      "Best for students, freshers and beginners who want to learn web development, build live projects and become job-ready developers.",
    keywords:
      "full stack web development course, React TypeScript course, Java Spring Boot course, Node.js backend course, REST API development, MySQL database course, web development for beginners, live project training",
    slug: "full-stack-web-development",
  },
  {
    title: "Java Backend Development",
    seoTitle: "Java Backend Development Course with Spring Boot REST APIs JWT and MySQL",
    category: "Full-Stack",
    desc: "Master Java backend development with Spring Boot, REST APIs, authentication, JWT security, database integration and production-ready architecture.",
    duration: "4 Months",
    level: "Intermediate",
    price: "₹4,500",
    tag: "Backend",
    features: [
      "Spring Boot",
      "REST API development",
      "JWT authentication",
      "MySQL integration",
    ],
    audience:
      "Best for learners who want to become backend developers and build secure APIs, admin panels, dashboards and database-driven applications.",
    keywords:
      "Java backend development course, Spring Boot online course, REST API course, JWT authentication, MySQL integration, backend developer training, Java API development, backend project course",
    slug: "java-backend-development",
  },
  {
    title: "Algo Trading Mastery",
    seoTitle: "Algo Trading Course for Beginners with Strategy Risk Management and Automation",
    category: "Algo Trading",
    desc: "Learn trading automation basics, strategy logic, risk management, dashboard flow and practical examples for algorithmic trading systems.",
    duration: "3 Months",
    level: "Beginner",
    price: "₹5,999",
    tag: "Trading",
    features: [
      "Strategy basics",
      "Risk control",
      "Dashboard flow",
      "Live examples",
    ],
    audience:
      "Best for learners who want to understand algo trading basics, automated strategy flow, trading dashboard logic and risk-controlled execution.",
    keywords:
      "algo trading course, algorithmic trading for beginners, trading automation course, stock market automation, trading strategy course, algo trading dashboard, risk management trading, automated trading basics",
    slug: "algo-trading-mastery",
  },
  {
    title: "JEE + NEET Test Series",
    seoTitle: "JEE and NEET Online Test Series with Chapter Tests and Full Mock Exams",
    category: "Test Series",
    desc: "Chapter-wise and full-length online mock tests for JEE and NEET designed according to real exam patterns with score and rank analysis.",
    duration: "1 Year Access",
    level: "All Levels",
    price: "₹1,999",
    tag: "Tests",
    features: [
      "Chapter-wise tests",
      "Full mock exams",
      "Score analysis",
      "Rank report",
    ],
    audience:
      "Best for JEE Main, JEE Advanced and NEET UG aspirants who want regular practice, speed improvement, accuracy tracking and exam readiness.",
    keywords:
      "JEE test series, NEET test series, online mock test, JEE Main mock test, NEET mock exam, chapter wise test, full length test, score analysis, rank report",
    slug: "jee-neet-test-series",
  },
  {
    title: "1:1 Mentor Support Program",
    seoTitle: "Personal Mentorship Program for Students with Study Plan and Progress Tracking",
    category: "Mentorship",
    desc: "Personalized academic guidance with study roadmap, weekly review, progress tracking, time management and subject-wise doubt support.",
    duration: "Monthly",
    level: "Personalized",
    price: "₹999/mo",
    tag: "Support",
    features: [
      "Personal study roadmap",
      "Weekly progress review",
      "Doubt support",
      "Progress tracking",
    ],
    audience:
      "Best for students who need personal guidance, study planning, exam strategy, motivation, timetable support and regular performance review.",
    keywords:
      "student mentorship program, academic mentor support, study plan for students, personal guidance for JEE NEET, weekly progress review, doubt support program, exam preparation roadmap",
    slug: "one-to-one-mentor-support-program",
  },
];

const seoKeywords = [
  "Mediit Academy courses",
  "online courses for students",
  "Class 7 online classes",
  "Class 8 online tuition",
  "Class 9 online coaching",
  "Class 10 board exam preparation",
  "CBSE online classes",
  "NCERT online course",
  "school foundation course",
  "JEE Main online coaching",
  "JEE Advanced preparation",
  "IIT JEE course",
  "NEET UG online coaching",
  "medical entrance coaching",
  "NEET Biology course",
  "Physics Chemistry Maths coaching",
  "Physics Chemistry Biology coaching",
  "online mock test series",
  "JEE NEET test series",
  "student mentorship program",
  "full stack web development course",
  "React TypeScript course",
  "Java Spring Boot course",
  "Node.js backend course",
  "REST API development course",
  "algo trading course",
  "algorithmic trading for beginners",
  "live online classes",
  "study notes",
  "doubt solving",
  "performance tracking",
].join(", ");

const CoursesPage: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<CourseCategory>("All");

  const filteredCourses = useMemo(() => {
    if (activeCategory === "All") return courses;
    return courses.filter((course) => course.category === activeCategory);
  }, [activeCategory]);

  const courseSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Mediit Academy Online Courses",
      description:
        "Explore Mediit Academy courses for Class 7 to 10 foundation, CBSE board preparation, JEE Main, JEE Advanced, NEET UG, Full-Stack Web Development, Algo Trading, online test series and personal mentorship.",
      url: "/courses",
      about: [
        "School Foundation Courses",
        "CBSE NCERT Online Classes",
        "JEE Main Online Coaching",
        "JEE Advanced Preparation",
        "NEET UG Online Coaching",
        "Full-Stack Web Development Course",
        "Java Backend Development Course",
        "Algo Trading Course",
        "Online Mock Test Series",
        "Student Mentorship Program",
      ],
      mainEntity: {
        "@type": "ItemList",
        name: "Online Course List",
        itemListElement: courses.map((course, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "Course",
            name: course.seoTitle,
            description: course.desc,
            provider: {
              "@type": "EducationalOrganization",
              name: "Mediit Academy",
              url: "/",
            },
            educationalLevel: course.level,
            timeRequired: course.duration,
            audience: {
              "@type": "EducationalAudience",
              audienceType: course.audience,
            },
            keywords: course.keywords,
            offers: {
              "@type": "Offer",
              priceCurrency: "INR",
              price: course.price.replace(/[₹,/a-zA-Z]/g, ""),
              availability: "https://schema.org/InStock",
              url: `/courses/${course.slug}`,
            },
          },
        })),
      },
    }),
    []
  );

  useEffect(() => {
    const title =
      "Online Courses for Class 7-10, JEE, NEET, Full-Stack and Algo Trading | Mediit Academy";

    const description =
      "Explore Mediit Academy online courses for Class 7, Class 8, Class 9, Class 10, CBSE, NCERT, JEE Main, JEE Advanced, NEET UG, test series, mentorship, full-stack web development and algo trading.";

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", seoKeywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/courses`);
  }, []);

  const goToLogin = () => {
    window.location.href = "/login";
  };

  return (
    <div className="min-h-screen bg-white font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(courseSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-gradient-to-br from-[#f8fbff] via-white to-[#eef5ff]"
          aria-labelledby="courses-page-heading"
        >
          <div className="absolute left-10 top-20 h-40 w-40 rounded-full bg-blue-100 blur-3xl" />
          <div className="absolute bottom-10 right-20 h-44 w-44 rounded-full bg-orange-100 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-7">
                <p className="text-sm font-black uppercase tracking-[0.2em] text-blue-600">
                  Online Courses and Live Learning Programs
                </p>

                <h1
                  id="courses-page-heading"
                  className="mt-4 max-w-3xl text-4xl font-black leading-tight tracking-[-0.045em] text-slate-950 sm:text-5xl lg:text-[58px]"
                >
                  Online Courses for Class 7-10, JEE Main, NEET UG, Coding and
                  Test Series
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Explore live online classes, school foundation courses, CBSE
                  and NCERT preparation, JEE Main coaching, JEE Advanced
                  practice, NEET UG preparation, Full-Stack Web Development,
                  Algo Trading, online test series and personal mentorship
                  programs in one place.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-7 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Start learning with online courses for Class 7 to 10 JEE NEET coding and test series"
                    className="rounded-xl bg-[#101827] px-7 py-3 text-sm font-black text-white shadow-lg transition-all hover:-translate-y-0.5 hover:bg-slate-800"
                  >
                    Start Learning
                  </button>

                  <a
                    href="#course-list"
                    aria-label="View all online courses and course categories"
                    className="rounded-xl border border-slate-300 bg-white px-7 py-3 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5 hover:bg-slate-50"
                  >
                    View Courses
                  </a>
                </div>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[32px] border border-slate-100 bg-white p-5 shadow-[0_25px_80px_rgba(15,23,42,0.10)]">
                  <div className="rounded-[26px] bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950 p-6 text-white">
                    <p className="text-sm font-semibold text-white/70">
                      Featured Skill Program
                    </p>

                    <h2 className="mt-3 text-3xl font-black leading-tight">
                      Full-Stack Development and Algo Trading Mastery 2.0
                    </h2>

                    <p className="mt-4 text-sm leading-6 text-white/75">
                      Learn React, TypeScript, Java Spring Boot, Node.js APIs,
                      MySQL database, deployment, trading automation basics,
                      projects, assignments and mock tests.
                    </p>

                    <div className="mt-6 grid grid-cols-3 gap-3">
                      <div className="rounded-2xl bg-white/10 p-3 text-center">
                        <p className="text-xl font-black">6</p>
                        <p className="text-xs text-white/60">Months</p>
                      </div>

                      <div className="rounded-2xl bg-white/10 p-3 text-center">
                        <p className="text-xl font-black">Live</p>
                        <p className="text-xs text-white/60">Classes</p>
                      </div>

                      <div className="rounded-2xl bg-white/10 p-3 text-center">
                        <p className="text-xl font-black">₹4.9k</p>
                        <p className="text-xs text-white/60">Fee</p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={goToLogin}
                      aria-label="Enroll in Full Stack Development and Algo Trading Mastery program"
                      className="mt-6 w-full rounded-xl bg-white px-5 py-3 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5"
                    >
                      Enroll Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORY FILTERS */}
        <section id="course-list" className="bg-white py-10 font-['Inter']">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Course Categories
                </p>

                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Browse Courses by Class, Exam Goal and Skill
                </h2>
              </div>

              <p className="max-w-lg text-sm leading-6 text-slate-500">
                Select a category to find the right course according to your
                class, exam goal, subject requirement, learning level, career
                plan and practice needs.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Course category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  title={categorySeoText[category]}
                  aria-label={`Show ${category} courses. ${categorySeoText[category]}`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-400"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <p className="mb-6 text-sm leading-6 text-slate-500">
              {categorySeoText[activeCategory]}
            </p>

            {/* COURSE CARDS */}
            <div className="grid grid-cols-1 gap-5 font-['Inter'] md:grid-cols-2 xl:grid-cols-3">
              {filteredCourses.map((course) => (
                <article
                  key={course.title}
                  title={course.seoTitle}
                  className="group rounded-[26px] border border-slate-200 bg-white p-5 shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_18px_50px_rgba(15,23,42,0.10)]"
                >
                  <div className="mb-5 flex items-start justify-between gap-4">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-xl"
                      aria-hidden="true"
                    >
                      🎓
                    </div>

                    {course.tag && (
                      <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-600">
                        {course.tag}
                      </span>
                    )}
                  </div>

                  <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-600">
                    {course.category}
                  </p>

                  <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.03em] text-slate-950">
                    {course.title}
                  </h3>

                  <p className="mt-2 text-sm font-bold text-blue-700">
                    {course.seoTitle}
                  </p>

                  <p className="mt-3 min-h-[96px] text-sm leading-6 text-slate-600">
                    {course.desc}
                  </p>

                  <p className="sr-only">
                    {course.audience}. Keywords: {course.keywords}
                  </p>

                  <div className="mt-5 grid grid-cols-3 gap-3">
                    <div className="rounded-2xl bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Duration</p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {course.duration}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Level</p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {course.level}
                      </p>
                    </div>

                    <div className="rounded-2xl bg-slate-50 p-3">
                      <p className="text-xs text-slate-500">Fee</p>
                      <p className="mt-1 text-sm font-black text-slate-950">
                        {course.price}
                      </p>
                    </div>
                  </div>

                  <div className="mt-5 space-y-2">
                    {course.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <span
                          className="flex h-5 w-5 items-center justify-center rounded-full bg-green-50 text-xs text-green-600"
                          aria-hidden="true"
                        >
                          ✓
                        </span>
                        <p className="text-sm font-medium text-slate-700">
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button
                      type="button"
                      onClick={goToLogin}
                      aria-label={`Enroll now in ${course.seoTitle}`}
                      className="flex-1 rounded-xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all hover:bg-slate-800"
                    >
                      Enroll Now
                    </button>

                    <a
                      href={`/courses/${course.slug}`}
                      aria-label={`View details of ${course.seoTitle}`}
                      className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-black text-slate-950 transition-all hover:bg-slate-50"
                    >
                      Details
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-slate-50 py-12 font-['Inter']">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="rounded-[32px] bg-gradient-to-r from-slate-950 via-slate-900 to-orange-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-3xl font-black tracking-[-0.035em]">
                    Confused About Which Course to Choose?
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Talk to our academic counselor and get a personalized
                    roadmap based on your class, exam goal, subject level, study
                    schedule, career interest and preparation target.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={goToLogin}
                  aria-label="Get free course guidance and academic counseling"
                  className="rounded-xl bg-white px-7 py-3 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5"
                >
                  Get Free Guidance
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CoursesPage;